import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActioCommandProgressInfo, ActionCommand, ActionCommandResponse } from 'src/app/interfaces/action-command';
import { ServerTypes } from 'src/app/models/servers';
import { HttpBaseService } from 'src/app/pages/admin/cust-admin/http-base.service';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, Subject, firstValueFrom } from 'rxjs';
import { ApiService } from './api.service';
import { StorageService } from './storage.service';

export interface CommandResult {
  isSuccess: boolean;
  messageId?: string;
  error?: string;
}

export interface ProgressInfo {
  messageId: string;
  isSuccess: boolean;
  progressInfo?: ActioCommandProgressInfo;
  error?: string;
}

export interface AutoRefreshSettings {
  autoRefresh: boolean,
  refreshInterval: number,
}

const PROGRESS_SETTINGS_TOKEN = 'PROGRESS_REFRESH_SETTINGS';

const COMMAND_PROGRESS_STATE_TOKEN = 'COMMAND_PROGRESS_STATE_TOKEN';
const COMMAND_HISTORY_STATE_TOKEN = 'COMMAND_HISTORY_STATE_TOKEN';
const TRACKED_MESSAGES_STATE_TOKEN = 'TRACKED_MESSAGES_STATE_TOKEN';
const COMMAND_RESULT_CACHE_TOKEN = 'COMMAND_RESULT_CACHE_TOKEN';
const CLIENTID_KEY = 'clientId'; // Todo: clientId key should be defined globaly.

// how many recent one-off command results (e.g. IDS_TASK_RUN) the bell's
// notification panel keeps around, newest first
const MAX_TRACKED_MESSAGES = 20;

// how many finished commands' results (messageType + commandResult etc.)
// getCommandProgress caches in localStorage, keyed by messageId
const MAX_CACHED_RESULTS = 50;

@Injectable({
  providedIn: 'root'
})
export class ActionCommandService extends HttpBaseService {

  private readonly commandEndpoint = 'v1/commands';
  private readonly messageEndpoint = 'v1/esb/messages';
  private readonly serverUrl = this.apiService.getCurentServer(ServerTypes.boapi);

  private commandProgressSubject: BehaviorSubject<ProgressInfo[]> = new BehaviorSubject<ProgressInfo[]>([]);
  private commandHistorySubject: BehaviorSubject<ProgressInfo[]> = new BehaviorSubject<ProgressInfo[]>([]);
  private refreshRunningSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private hasHistorySubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private hasBackgroundTasksSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private newEventsSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  private trackedMessageIdsSubject: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
  private currentMessageIdSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  private pendingSendSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private commandTrackedSubject: Subject<string> = new Subject<string>();
  private openRequestedSubject: Subject<void> = new Subject<void>();

  private commandHistory: ProgressInfo[] = [];
  private backgroundCommands: string[] = [];
  private trackedMessageIds: string[] = [];
  private resultCache: Record<string, ProgressInfo> = {};
  private isRefreshRunning: boolean = false;
  private newEventsCount: number = 0;
  private refreshSettings: AutoRefreshSettings;
  private autoRefreshToken;

  public commandProgress$: Observable<ProgressInfo[]> = this.commandProgressSubject.asObservable();
  public commandHistory$: Observable<ProgressInfo[]> = this.commandHistorySubject.asObservable();
  public isRefreshRunning$: Observable<boolean> = this.refreshRunningSubject.asObservable();
  public hasHistoryEntries$: Observable<boolean> = this.hasHistorySubject.asObservable();
  public hasBackgroundTasks$: Observable<boolean> = this.hasBackgroundTasksSubject.asObservable();
  public newEvents$: Observable<number> = this.newEventsSubject.asObservable();
  // recent one-off command results (e.g. IDS_TASK_RUN "Run task"), newest
  // first — rendered in the bell's notification panel via app-message-result
  public trackedMessageIds$: Observable<string[]> = this.trackedMessageIdsSubject.asObservable();
  // the message currently being shown "up front" by the bell — null while a
  // send is in flight (cleared by requestOpen so the previous command's
  // "Completed" state doesn't linger on screen), set once trackMessage runs
  public currentMessageId$: Observable<string | null> = this.currentMessageIdSubject.asObservable();
  // true from requestOpen() until trackMessage()/cancelPendingSend() — lets
  // the bell show a "Running…" placeholder even with no history yet
  public pendingSend$: Observable<boolean> = this.pendingSendSubject.asObservable();
  // fires only when a command is actively tracked this session (not when the
  // list is restored from localStorage on load) — lets the bell auto-open
  public commandTracked$: Observable<string> = this.commandTrackedSubject.asObservable();
  // fires the moment a command is about to be sent — lets the bell open
  // immediately instead of waiting for the send to round-trip
  public openRequested$: Observable<void> = this.openRequestedSubject.asObservable();

  constructor(
    protected http: HttpClient,
    private apiService: ApiService,
      private storageService: StorageService,
      private toastrService: ToastrService
  ) {
    super(http);
    this.refreshSettings = this.getAutoRefreshSettings();
    this.loadInitialState();
  }

  public async refreshCommandProgress() {
    if (this.isRefreshRunning) return;
    this.setRefreshRunning(true);

    this.commandProgressSubject.getValue().forEach(info => {
      if (!!info.progressInfo && (info.progressInfo.isCompleted || !!info.progressInfo.exception || info.progressInfo.isFailure)) this.addToCommandHistory(info);
    });

    if (this.backgroundCommands.length > 0) {
      let refresh = await Promise.all(this.backgroundCommands.map(messageId => this.getCommandProgress(messageId)));
      this.setBackgroundTasks(refresh);
    } else {
      this.setBackgroundTasks([]);
    }
    this.setRefreshRunning(false);

    if (!!this.refreshSettings.autoRefresh && this.backgroundCommands.length > 0) {
      this.autoRefreshToken = setTimeout(() => {
        this.refreshCommandProgress()
      }, this.refreshSettings.refreshInterval * 1000);
    }
  }

  public clearCommandHistory() {
    this.commandHistory = [];
    this.setCommandHistory();
  }

  public markEventsWatched() {
    this.newEventsCount = -1;
    this.registerEvent(); // will count up to zero again.
  }

  public getRefreshSettings(): AutoRefreshSettings {
    return this.refreshSettings;
  }

  public updateRefreshSettings(settings: AutoRefreshSettings) {
    this.setAutoRefreshSettings(settings);
    if (settings.autoRefresh && this.backgroundCommands.length > 0) {
      this.refreshCommandProgress();
    } else {
      this.cancelAutoRefresh();
    }

  }

  public sendCommand2(command: any): Observable<any> {
    return this.http.post(`${this.serverUrl}${this.commandEndpoint}`, command);
  }


  public async sendCommand(command: ActionCommand): Promise<CommandResult> {
    try {
        let response = await firstValueFrom(this.post<ActionCommandResponse>(`${this.serverUrl}${this.commandEndpoint}`, command), { defaultValue: undefined });
        return { isSuccess: true, messageId: response.messageId };
    } catch (err) {
        this.toastrService.error(err.error.message);
        return { isSuccess: false, error: err };
    }
  }
  
  public async getCommandProgress(messageId: string): Promise<ProgressInfo> {
    const cached = this.resultCache[messageId];
    if (cached) return cached;

    try {
      let info = await firstValueFrom(this.get<ActioCommandProgressInfo>(`${this.serverUrl}${this.messageEndpoint}/${messageId}/progress`), { defaultValue: undefined });
      let progressInfo = { messageId, isSuccess: true, progressInfo: info };
      if (!!info && info.isCompleted) this.registerEvent();
      this.cacheResultIfTerminal(progressInfo);
      return progressInfo;
    } catch (err) {
      console.error(err);
      return { messageId, isSuccess: false, error: err };
    }
  }

  public async pauseCommand(messageId: string): Promise<CommandResult> {
    try {
      let response = await firstValueFrom(this.put<any>(`${this.serverUrl}${this.messageEndpoint}/${messageId}/pause`, null), { defaultValue: undefined });
      return { isSuccess: true };
    } catch (err) {
      console.error(err);
      return { isSuccess: false, error: err };
    }
  }

  public async resumeCommand(messageId: string): Promise<CommandResult> {
    try {
      let response = await firstValueFrom(this.delete<any>(`${this.serverUrl}${this.messageEndpoint}/${messageId}/pause`), { defaultValue: undefined });
      return { isSuccess: true };
    } catch (err) {
      console.error(err);
      return { isSuccess: false, error: err };
    }
  }

  public async cancelCommand(messageId: string): Promise<CommandResult> {
    try {
      let response = await firstValueFrom(this.delete<any>(`${this.serverUrl}${this.messageEndpoint}/${messageId}`), { defaultValue: undefined });
      return { isSuccess: true };
    } catch (err) {
      console.error(err);
      return { isSuccess: false, error: err };
    }
  }

  public continueCommandInBackground(messageId: string) {
    if (!this.backgroundCommands.includes(messageId)) {
      this.setBackgroundCommands([...this.backgroundCommands, messageId]);
      this.registerEvent();
      if (!this.refreshSettings.autoRefresh) {
        this.refreshCommandProgress();
      }
    }
  }

  public requestOpen(): void {
    this.currentMessageIdSubject.next(null);
    this.pendingSendSubject.next(true);
    this.openRequestedSubject.next();
  }

  // call from a failed send so the bell doesn't stay stuck on "Running…"
  // forever when trackMessage() never ends up being called
  public cancelPendingSend(): void {
    this.pendingSendSubject.next(false);
  }

  public trackMessage(messageId: string) {
    this.trackedMessageIds = [messageId, ...this.trackedMessageIds.filter(id => id !== messageId)].slice(0, MAX_TRACKED_MESSAGES);
    this.trackedMessageIdsSubject.next(this.trackedMessageIds);
    this.currentMessageIdSubject.next(messageId);
    this.pendingSendSubject.next(false);
    this.saveState(TRACKED_MESSAGES_STATE_TOKEN, this.trackedMessageIds);
    this.commandTrackedSubject.next(messageId);
  }

  public addToCommandHistory(progressInfo: ProgressInfo) {
    let internal = false;
    if (this.backgroundCommands.includes(progressInfo.messageId)) {
      this.setBackgroundCommands(this.backgroundCommands.filter(id => id != progressInfo.messageId));
      internal = true;
    }
    if (!this.commandHistory.some((hist: ProgressInfo) => hist.messageId == progressInfo.messageId)) {
      this.commandHistory.unshift(progressInfo);
      if (!internal) this.registerEvent();
    }
    this.setCommandHistory();
  }

  // once a command reaches a terminal state (completed/failed/timed out) its
  // result never changes, so it's safe to cache — later getCommandProgress
  // calls for the same messageId are then served from localStorage instead
  // of hitting the API again
  private cacheResultIfTerminal(progress: ProgressInfo): void {
    const info = progress?.progressInfo;
    const terminal = progress.isSuccess && !!info && (info.isCompleted || info.isFailure || !!info.exception);
    if (!terminal) return;

    this.resultCache[progress.messageId] = progress;
    const keys = Object.keys(this.resultCache);
    if (keys.length > MAX_CACHED_RESULTS) {
      delete this.resultCache[keys[0]];
    }
    this.saveState(COMMAND_RESULT_CACHE_TOKEN, this.resultCache);
  }

  private registerEvent() {
    this.newEventsSubject.next(++this.newEventsCount);
  }

  private cancelAutoRefresh() {
    if (!!this.autoRefreshToken) clearTimeout(this.autoRefreshToken);
  }

  private setBackgroundTasks(progress: ProgressInfo[]) {
    this.commandProgressSubject.next(progress);
    this.hasBackgroundTasksSubject.next(this.backgroundCommands.length > 0);
  }

  private setBackgroundCommands(commands: string[]) {
    this.backgroundCommands = commands ?? [];
    this.saveState(COMMAND_PROGRESS_STATE_TOKEN, this.backgroundCommands);
  }

  private setCommandHistory() {
    this.commandHistorySubject.next(this.commandHistory);
    this.hasHistorySubject.next(this.commandHistory.length > 0);
    this.saveState(COMMAND_HISTORY_STATE_TOKEN, this.commandHistorySubject.getValue());
  }

  private setRefreshRunning(running: boolean) {
    this.isRefreshRunning = running;
    this.refreshRunningSubject.next(this.isRefreshRunning);
  }

  private getAutoRefreshSettings(): AutoRefreshSettings {
    let refreshSettings = JSON.parse(this.storageService.getLocalStorageItem(PROGRESS_SETTINGS_TOKEN));
    return !!(refreshSettings as AutoRefreshSettings) ? refreshSettings : { autoRefresh: false, refreshInterval: 20 };
  }

  private setAutoRefreshSettings(settings: AutoRefreshSettings) {
    this.refreshSettings = settings;
    this.storageService.setLocalStorageItem(PROGRESS_SETTINGS_TOKEN, JSON.stringify(settings));
  }

  private saveState(stateToken: string, value: any) {
    const clientId = this.storageService.getLocalStorageItem(CLIENTID_KEY);
    this.storageService.setLocalStorageItem(`${stateToken}_${clientId}`, JSON.stringify(value));
  }
  
  private loadInitialState() {
    const clientId = this.storageService.getLocalStorageItem(CLIENTID_KEY);

    let savedHistoryState = JSON.parse(this.storageService.getLocalStorageItem(`${COMMAND_HISTORY_STATE_TOKEN}_${clientId}`));
    this.commandHistory = Array.isArray(savedHistoryState) ? savedHistoryState : [];
    this.setCommandHistory();

    let savedProgressState = JSON.parse(this.storageService.getLocalStorageItem(`${COMMAND_PROGRESS_STATE_TOKEN}_${clientId}`));
    this.backgroundCommands = Array.isArray(savedProgressState) ? savedProgressState : [];
    this.refreshCommandProgress()

    let savedTrackedMessages = JSON.parse(this.storageService.getLocalStorageItem(`${TRACKED_MESSAGES_STATE_TOKEN}_${clientId}`));
    this.trackedMessageIds = Array.isArray(savedTrackedMessages) ? savedTrackedMessages : [];
    this.trackedMessageIdsSubject.next(this.trackedMessageIds);
    // default "current" to the most recent restored message, so a page
    // reload still shows the last known result instead of a blank panel
    this.currentMessageIdSubject.next(this.trackedMessageIds[0] ?? null);

    let savedResultCache = JSON.parse(this.storageService.getLocalStorageItem(`${COMMAND_RESULT_CACHE_TOKEN}_${clientId}`));
    this.resultCache = savedResultCache && typeof savedResultCache === 'object' ? savedResultCache : {};
  }

}
